import React, { useEffect, useState } from 'react';
import { Checkbox, Divider, Tooltip } from 'antd';
import { FabricObject } from 'fabric';
import { TextSettings } from '../../text';
import { ShapeSettings } from '../../selection/components/shape-settings';
import { ImageSettings } from '../../media/components/image-settings';
import { CUSTOM_COMPONENT_REGISTRY, SettingsLayout } from '../custom-component-registry';
import { C } from '../../constants/design-tokens';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { usePanels } from '../../shared/hooks/use-panels';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { FABRIC_TYPE } from '../../canvas/constants/object-meta';
import { isSystemPiece } from '../../canvas/utils/canvas-pieces';
import { inspectorPanelStyles } from '../styles';

const LinkAcrossResolutionsToggle: React.FC<{ selectedObject: FabricObject; vertical: boolean }> = ({
  selectedObject,
  vertical
}) => {
  const [linked, setLinked] = useState(selectedObject.linkedAcrossResolutions !== false);

  useEffect(() => {
    setLinked(selectedObject.linkedAcrossResolutions !== false);
  }, [selectedObject]);

  const handleChange = (checked: boolean) => {
    setLinked(checked);
    selectedObject.set('linkedAcrossResolutions', checked);
  };

  return (
    <Tooltip title='Sincronizar con otras resoluciones' placement='top'>
      <Checkbox checked={linked} onChange={e => handleChange(e.target.checked)} style={{ fontSize: 12 }}>
        Sync
      </Checkbox>
      {vertical ? <Divider style={{ background: C.panelBorder, margin: '15px 0' }} /> : null}
    </Tooltip>
  );
};

type SettingsEntry = { Component: React.FC; layout: SettingsLayout };

// Built-in Fabric types dispatch by obj.type; custom components register in CUSTOM_COMPONENT_REGISTRY.
const SETTINGS_BY_TYPE: Record<string, SettingsEntry> = {
  [FABRIC_TYPE.iText]: { Component: TextSettings, layout: 'toolbar' },
  [FABRIC_TYPE.textbox]: { Component: TextSettings, layout: 'toolbar' },
  [FABRIC_TYPE.rect]: { Component: ShapeSettings, layout: 'toolbar' },
  [FABRIC_TYPE.circle]: { Component: ShapeSettings, layout: 'toolbar' },
  [FABRIC_TYPE.image]: { Component: ImageSettings, layout: 'panel' },
  ...Object.fromEntries(CUSTOM_COMPONENT_REGISTRY.map(c => [c.key, { Component: c.Component, layout: c.layout }]))
};

const settingsKeyFor = (obj: FabricObject): string =>
  CUSTOM_COMPONENT_REGISTRY.find(c => c.isMatch(obj))?.key ?? obj.type;

export const isTopToolbarLayout = (obj: FabricObject | null): boolean =>
  !!obj && SETTINGS_BY_TYPE[settingsKeyFor(obj)]?.layout === 'toolbar';

const toolbarStyles: React.CSSProperties = {
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  top: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'max-content',
  maxWidth: '90%',
  padding: '8px 16px',
  background: C.panelBg,
  color: C.textPrimary,
  border: `1px solid ${C.panelBorder}`,
  borderRadius: 14,
  boxShadow: '0 2px 20px rgba(0, 0, 0, 0.10)',
  zIndex: 10
};

export const SettingsPanel: React.FC = () => {
  const { canvas, selectedObject } = useEditorCanvas();
  const { isOpen } = usePanels();
  const { resolutions } = useResolutions();

  if (!canvas || !selectedObject) return null;

  // System pieces (backgrounds, effect overlays) have dedicated panels.
  if (isSystemPiece(selectedObject)) return null;

  const entry = SETTINGS_BY_TYPE[settingsKeyFor(selectedObject)];
  const showLinkToggle = !!selectedObject.pieceId && resolutions.length > 1;
  if (!entry && !showLinkToggle) return null;

  const layout = entry?.layout ?? 'panel';

  // Panel layout shares the inspector slot with the effects panel — hide
  // when effects is open. Toolbar layout lives on top and can coexist.
  if (layout === 'panel' && isOpen('effects')) return null;
  const containerStyle: React.CSSProperties =
    layout === 'toolbar' ? toolbarStyles : { ...inspectorPanelStyles, padding: 16 };

  const Settings = entry?.Component;

  return (
    <div style={containerStyle}>
      {showLinkToggle && (
        <LinkAcrossResolutionsToggle selectedObject={selectedObject} vertical={layout !== 'toolbar'} />
      )}
      {Settings && <Settings />}
    </div>
  );
};
